import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import { listCustomers, listShipTos } from '../customers/api';
import { createSalesOrder } from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  onCreated: (salesOrderId: string) => void;
}

/**
 * Starting an order: who it is for, and where it goes.
 *
 * Only the customer is required. Everything else on an order — the lines, the freight, the dates —
 * is added on the detail screen, because an order is a document worked on over minutes rather than
 * a row filled in and saved. Asking for all of it up front would make the common case (somebody on
 * the phone, typing while the customer talks) slower, not safer.
 *
 * The warehouse is not asked for at all. The server takes it from the session.
 */
export function NewSalesOrderModal({ opened, onClose, onCreated }: Props) {
  const { t } = useTranslation();

  const [customerId, setCustomerId] = useState<string | null>(null);
  const [shipToId, setShipToId] = useState<string | null>(null);
  const [customerPo, setCustomerPo] = useState('');
  const [orderedBy, setOrderedBy] = useState('');
  const [acceptPartial, setAcceptPartial] = useState(true);
  const [requestedShipDate, setRequestedShipDate] = useState('');
  const [instructions, setInstructions] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [customerError, setCustomerError] = useState<string | null>(null);

  const customers = useQuery({
    queryKey: ['customers', 'active'] as const,
    queryFn: ({ signal }) => listCustomers({}, signal),
    enabled: opened,
  });

  // Fetched per customer, not filtered from a cached list: the addresses belong to the customer
  // and there is no list of everybody's.
  const shipTos = useQuery({
    queryKey: ['customer-ship-tos', customerId] as const,
    queryFn: ({ signal }) => listShipTos(customerId!, signal),
    enabled: opened && Boolean(customerId),
  });

  useEffect(() => {
    if (!opened) return;
    setCustomerId(null);
    setShipToId(null);
    setCustomerPo('');
    setOrderedBy('');
    setAcceptPartial(true);
    setRequestedShipDate('');
    setInstructions('');
    setError(null);
    setCustomerError(null);
  }, [opened]);

  // The customer's default, pre-selected. The server would apply it anyway when none is sent, but
  // showing it means somebody can SEE where the order is going before they commit to it.
  useEffect(() => {
    if (!shipTos.data) return;
    const preferred = shipTos.data.find((s) => s.isDefault) ?? null;
    setShipToId(preferred?.customerShipToId ?? null);
  }, [shipTos.data]);

  const customerOptions = useMemo(
    () =>
      (customers.data ?? []).map((c) => ({
        value: c.customerId,
        label: `${c.customerNumber} — ${c.customerName}`,
      })),
    [customers.data],
  );

  const shipToOptions = useMemo(
    () =>
      (shipTos.data ?? []).map((s) => ({
        value: s.customerShipToId,
        label: `${s.shipToCode} — ${s.shipToName}`,
      })),
    [shipTos.data],
  );

  const create = useMutation({
    mutationFn: () =>
      createSalesOrder({
        customerId: customerId!,
        customerShipToId: shipToId,
        customerPo: blankToNull(customerPo),
        orderedBy: blankToNull(orderedBy),
        acceptPartial,
        requestedShipDateUtc: requestedShipDate ? new Date(requestedShipDate).toISOString() : null,
        specialInstructions: blankToNull(instructions),
      }),
    onSuccess: (order) => {
      onCreated(order.salesOrderId);
      onClose();
    },
    onError: (err) => setError(describe(err)),
  });

  function describe(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.problem.type?.includes('customer_not_in_warehouse')) {
        return t('salesOrders.errors.customerNotHere');
      }
      if (err.problem.type?.includes('customer_inactive')) {
        return t('salesOrders.errors.customerInactive');
      }
      return err.problem.detail ?? err.problem.title ?? t('salesOrders.errors.createFailed');
    }
    return t('salesOrders.errors.createFailed');
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!customerId) {
      setCustomerError(t('salesOrders.errors.customerRequired'));
      return;
    }
    setCustomerError(null);
    create.mutate();
  }

  const noCustomers = customers.isSuccess && customerOptions.length === 0;

  return (
    <AppModal opened={opened} onClose={onClose} title={t('salesOrders.newTitle')} width="md">
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          {noCustomers && (
            // The dead end this screen can reach: an order needs somebody to be for, and the
            // customer screen is where that starts.
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
              {t('salesOrders.noCustomersYet')}
            </Alert>
          )}

          <Select
            label={t('salesOrders.customer')}
            placeholder={t('salesOrders.customerPlaceholder')}
            data={customerOptions}
            value={customerId}
            onChange={setCustomerId}
            error={customerError}
            disabled={customers.isLoading || noCustomers}
            searchable
            required
          />

          <Select
            label={t('salesOrders.shipTo')}
            description={
              customerId && shipToOptions.length === 0 && !shipTos.isLoading
                ? t('salesOrders.noShipTos')
                : t('salesOrders.shipToHint')
            }
            placeholder={t('salesOrders.shipToPlaceholder')}
            data={shipToOptions}
            value={shipToId}
            onChange={setShipToId}
            disabled={!customerId || shipTos.isLoading || shipToOptions.length === 0}
            searchable
            clearable
          />

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label={t('salesOrders.customerPo')}
              description={t('salesOrders.customerPoHint')}
              value={customerPo}
              onChange={(e) => setCustomerPo(e.currentTarget.value)}
              maxLength={50}
            />
            <TextInput
              label={t('salesOrders.orderedBy')}
              description={t('salesOrders.orderedByHint')}
              value={orderedBy}
              onChange={(e) => setOrderedBy(e.currentTarget.value)}
              maxLength={100}
            />
          </SimpleGrid>

          <TextInput
            label={t('salesOrders.requested')}
            type="date"
            value={requestedShipDate}
            onChange={(e) => setRequestedShipDate(e.currentTarget.value)}
          />

          <Textarea
            label={t('salesOrders.instructions')}
            value={instructions}
            onChange={(e) => setInstructions(e.currentTarget.value)}
            autosize
            minRows={2}
            maxRows={5}
          />

          <Switch
            label={t('salesOrders.acceptPartial')}
            // Not a note. It decides what happens when the pick comes up short -- ship what there
            // is, or hold the whole order -- so it belongs on the form that raises the order.
            description={t('salesOrders.acceptPartialHint')}
            checked={acceptPartial}
            onChange={(e) => setAcceptPartial(e.currentTarget.checked)}
          />

          {error && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              <Text size="sm">{error}</Text>
            </Alert>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {t('shell.cancel')}
            </Button>
            <Button type="submit" color="invetrak" loading={create.isPending} disabled={noCustomers}>
              {t('salesOrders.createAndOpen')}
            </Button>
          </Group>
        </Stack>
      </form>
    </AppModal>
  );
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}
